import React, { CSSProperties, useState, useMemo, useEffect } from 'react';
import { clsx, styx } from 'modules/Utils';
import InputSymphony, { SelectOptionItem } from 'elements/InputSymphony';
import ButtonSymphony from 'elements/ButtonSymphony';
import FileChooser from 'elements/FileChooser';
import SliderSymphony from 'elements/SliderSymphony';
import ModalSymphony from 'elements/ModalSymphony';
import TagSymphony, { TagColors } from 'elements/TagSymphony';
import Colors from 'modules/Colors';
import Sty from 'modules/Sty';
import dayjs from 'dayjs';
import { getVideoFromUrl_api, ImportFromURLResponse } from 'api/agentApi';
import { toastError, toastSuccess } from 'modules/Toast';
import { MediaType, RecordDetails } from 'modules/Types';
import { BiLink, BiImage, BiDollar, BiCheck, BiPlay, BiMusic, BiX } from 'react-icons/bi';
import { HiCursorClick } from 'react-icons/hi';
import useScreen from 'Hooks/useScreen';
import RecordSearchView from 'elements/RecordSearchView';
import useCampaignBuilder from 'Hooks/useCampaignBuilder';
import { AdPlatform, PlaybookType } from 'modules/Const';
import { AssetPlatformType } from 'components/CampaignViews/AdCreativeSelectorCampaignView';
import { useHistory } from 'react-router-dom';
import useBrand, { BrandExistsState } from 'Hooks/useBrand';
import DebugBox from './shareable/DebugBox';
import CloudinaryUploadWidget, { CloudinaryUploadedFileType } from 'components/CampaignViews/CloudinaryUploadWidget';
import AdPreviewCampaignView from 'components/CampaignViews/AdPreviewCampaignView';
import { motion } from 'framer-motion';
import { searchSpotifyReleases, searchSpotifyReleasesAdBox } from 'api/platformApi';
import { track } from 'analytics';

interface LinkData {
    url: string;
    title: string;
    description?: string;
    thumbnail?: string;
    type: 'spotify' | 'website' | 'other';
    recordDetails?: RecordDetails;
}

// Add Spotify metadata interface
interface SpotifyMetadata {
    trackName: string;
    artistName: string;
    spotifyUrl: string;
}

interface AdAsset {
    id: string;
    url: string;
    type: 'image' | 'video';
    name: string;
    selected: boolean;
    source: 'upload' | 'import';
    platform?: AssetPlatformType;
    thumbnailUrl?: string;
}

interface AdFormData {
    destinationLink: string;
    linkData: LinkData | null;
    assets: AdAsset[];
    budget: number;
    campaignLength: number;
}

interface AdCreatorPageProps {
    className?: string;
    style?: CSSProperties;
    onSubmit?: (data: AdFormData) => void;
    styleLayout?: CSSProperties;
}

const AdCreatorPage = ({
    className,
    style,
    styleLayout
}: AdCreatorPageProps) => {
    const { mobileView, mStyle, cssCalc } = useScreen();
    const history = useHistory();
    const { currentBrand } = useBrand() as BrandExistsState;
    const {
        initializeCampaignState,
        setAdPlatform,
        setBudget,
        setStartDate,
        linksToPromote,
        setLinksToPromote,
        setEndDate,
        setSelectedSongToPromote,
        uploadedAssets,
        setUploadedAssets,
        removeUploadedAsset,
        importedFromUrlPosts,
        setImportedFromUrlPosts,
        removeImportedFromUrlPost,
        submitCampaignFromAdBox,
        isSubmittingCampaign,
        playbookType,
        adPlatform,
        getDataForSubmission,
        setNumberOfDays
    } = useCampaignBuilder();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [destinationLink, setDestinationLink] = useState<string>('');
    const [assets, setAssets] = useState<AdAsset[]>([]);
    const [budget, setBudgetLocal] = useState<number>(100);
    const [campaignLength, setCampaignLength] = useState<number>(7);
    const [hasValidLink, setHasValidLink] = useState<boolean>(false);

    // Add state for Spotify metadata
    const [spotifyMetadata, setSpotifyMetadata] = useState<SpotifyMetadata | null>(null);

    // Add content type state
    const [selectedContentType, setSelectedContentType] = useState<'music' | 'podcast' | 'youtube' | 'shopify' | 'website' | 'event' | 'ticket' | 'newsletter' | null>(null);

    // Add state for animated label
    const [labelText, setLabelText] = useState<string>('');
    const [labelKey, setLabelKey] = useState<number>(0);

    // Song selector modal
    const [showSongSelectorModal, setShowSongSelectorModal] = useState<boolean>(false);

    // Link import states
    const [showLinkImportModal, setShowLinkImportModal] = useState<boolean>(false);
    const [linkImportInput, setLinkImportInput] = useState<string>('');
    const [isImportingFromLink, setIsImportingFromLink] = useState<boolean>(false);
    const [linkImportType, setLinkImportType] = useState<'general' | 'instagram' | 'tiktok'>('general');

    // Initialize campaign state on mount
    useEffect(() => {
        setAdPlatform(AdPlatform.META);
        initializeCampaignState({
            playbookType: PlaybookType.INCREASE_LINK_CLICKS,
        });
    }, []);

    // Update campaign builder budget when local budget changes
    useEffect(() => {
        setBudget(budget.toString());
    }, [budget]);

    // Track budget changes (debounced)
    useEffect(() => {
        // Don't track the initial budget set
        if (budget === 100) return;

        const timeoutId = setTimeout(() => {
            track('ADBOX Budget Changed', {
                version: 'v1',
                newBudget: budget,
                dailyBudget: Math.max(10, Math.floor(budget / campaignLength)),
                campaignLength: campaignLength,
                timestamp: new Date().toISOString()
            });
        }, 1000); // Debounce by 1 second

        return () => clearTimeout(timeoutId);
    }, [budget, campaignLength]);

    // Sync assets with campaign builder
    useEffect(() => {
        // Transform assets to match CloudinaryUploadedFileType
        const uploadedAssetsToSync = assets
            .filter(asset => asset.source === 'upload')
            .map((asset): CloudinaryUploadedFileType => ({
                id: asset.id,
                batchId: '',
                asset_id: asset.id,
                public_id: asset.id,
                version: 1,
                version_id: '1',
                signature: '',
                width: 1080,
                height: 1080,
                format: asset.type === 'video' ? 'mp4' : 'jpg',
                resource_type: asset.type,
                created_at: new Date().toISOString(),
                bytes: 0,
                type: 'upload',
                etag: '',
                placeholder: false,
                url: asset.url,
                secure_url: asset.url,
                access_mode: 'public',
                original_filename: asset.name,
                path: asset.url,
                thumbnail_url: asset.url,
                tags: [],
                folder: '',
                existing: false,
                platformType: AssetPlatformType.CLOUDINARY
            }));

        // Update imported posts for Instagram and TikTok imports only
        const importedPosts = assets
            .filter(asset =>
                asset.source === 'import' &&
                (asset.platform === AssetPlatformType.INSTAGRAM || asset.platform === AssetPlatformType.TIKTOK)
            )
            .map((asset): ImportFromURLResponse => ({
                id: asset.id,
                assetUrl: asset.url,
                mediaType: asset.type === 'video' ? MediaType.VIDEO : MediaType.IMAGE,
                platformCaption: asset.name,
                thumbnailUrl: asset.thumbnailUrl || asset.url,
                timestamp: new Date().toISOString(),
                originalPostType: asset.platform as (AssetPlatformType.INSTAGRAM | AssetPlatformType.TIKTOK),
                ownerUsername: '',
                platformType: AssetPlatformType.IMPORTED_FROM_URL,
            }));

        // Handle general URL imports as uploaded assets
        const generalImportAssetsToSync = assets
            .filter(asset =>
                asset.source === 'import' &&
                asset.platform !== AssetPlatformType.INSTAGRAM &&
                asset.platform !== AssetPlatformType.TIKTOK
            )
            .map((asset): CloudinaryUploadedFileType => ({
                id: asset.id,
                batchId: '',
                asset_id: asset.id,
                public_id: asset.id,
                version: 1,
                version_id: '1',
                signature: '',
                width: 1080,
                height: 1080,
                format: asset.type === 'video' ? 'mp4' : 'jpg',
                resource_type: asset.type,
                created_at: new Date().toISOString(),
                bytes: 0,
                type: 'upload',
                etag: '',
                placeholder: false,
                url: asset.url,
                secure_url: asset.url,
                access_mode: 'public',
                original_filename: asset.name,
                path: asset.url,
                thumbnail_url: asset.url,
                tags: [],
                folder: '',
                existing: false,
                platformType: AssetPlatformType.CLOUDINARY
            }));

        setUploadedAssets([...uploadedAssetsToSync, ...generalImportAssetsToSync]);
        setImportedFromUrlPosts(importedPosts);
    }, [assets, setUploadedAssets, setImportedFromUrlPosts]);

    /** Calculate daily budget */
    const dailyBudget = useMemo(() => {
        return Math.max(10, Math.floor(budget / campaignLength));
    }, [budget, campaignLength]);

    /** Handle campaign length change */
    const handleCampaignLengthChange = (value: string) => {
        const length = parseInt(value);
        if (!isNaN(length)) {
            // Update campaign length
            setCampaignLength(length);

            // Adjust budget if below minimum ($10/day)
            const minBudget = length * 10;
            if (budget < minBudget) {
                setBudgetLocal(minBudget);
            }

            // Update campaign dates
            const startDate = dayjs();
            const endDate = startDate.add(length, 'day');
            setStartDate(startDate.format('YYYY-MM-DD'));
            setEndDate(endDate.format('YYYY-MM-DD'));
            setNumberOfDays(length);
        }
    };

    /** Campaign length options */
    const campaignLengthOptions: SelectOptionItem[] = [
        { title: '7 days', value: '7' },
        // { title: '14 days', value: '14' },
        // { title: '30 days', value: '30' },
    ];

    /** Budget slider marks with better descriptions */
    const budgetMarks = [
        { value: 100, label: '$100', description: '~500 clicks' },
        { value: 200, label: '$200', description: '~1K clicks' },
        { value: 300, label: '$300', description: '~1.5K clicks' },
        { value: 400, label: '$400', description: '~2K clicks' },
        { value: 500, label: '$500', description: '~2.5K clicks' }
    ];

    /** Detect link type */
    const detectLinkType = (url: string): 'spotify' | 'website' | 'other' => {
        if (url.includes('spotify.com')) return 'spotify';
        if (url.includes('http') || url.includes('www')) return 'website';
        return 'other';
    };

    /** Enhanced link type detection for multiple platforms */
    const detectPlatformType = (url: string): typeof selectedContentType => {
        if (url.includes('spotify.com')) return 'music';
        if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube';
        if (url.includes('shopify.com') || url.includes('.myshopify.com')) return 'shopify';
        if (url.includes('podcast') || url.includes('podcasts.apple.com') || url.includes('podcasts.google.com')) return 'podcast';
        if (url.includes('eventbrite.com') || url.includes('ticketmaster.com')) return 'event';
        if (url.includes('stubhub.com') || url.includes('vivid') || url.includes('ticket')) return 'ticket';
        return 'website';
    };

    /** Get placeholder text based on content type */
    const getPlaceholderText = (): string => {
        switch (selectedContentType) {
            case 'music':
                return 'https://open.spotify.com/track/...';
            case 'podcast':
                return 'https://podcasts.apple.com/podcast/...';
            case 'youtube':
                return 'https://www.youtube.com/watch?v=...';
            case 'shopify':
                return 'https://yourstore.myshopify.com/products/...';
            case 'event':
                return 'https://www.eventbrite.com/e/...';
            case 'ticket':
                return 'https://www.ticketmaster.com/event/...';
            case 'newsletter':
                return 'https://yoursite.com/newsletter';
            default:
                return 'Enter your link to promote';
        }
    };

    /** Get dynamic CTA text based on platform */
    const getDynamicCTA = (): string => {
        const platform = detectPlatformType(destinationLink);
        switch (platform) {
            case 'music':
                return 'Listen Now';
            case 'podcast':
                return 'Play Episode';
            case 'youtube':
                return 'Watch Now';
            case 'shopify':
                return 'Shop Now';
            case 'event':
                return 'Learn More';
            case 'ticket':
                return 'Get Tickets';
            case 'newsletter':
                return 'Subscribe';
            default:
                return 'Learn More';
        }
    };

    /** Validate URL */
    const isValidUrl = (urlString: string): boolean => {
        try {
            // Check if it's a valid URL
            const url = new URL(urlString);
            // Ensure it has http or https protocol
            return url.protocol === 'http:' || url.protocol === 'https:';
        } catch {
            // Also accept URLs without protocol if they look like domains
            if (urlString.includes('.') && !urlString.includes(' ')) {
                try {
                    new URL(`https://${urlString}`);
                    return true;
                } catch {
                    return false;
                }
            }
            return false;
        }
    };

    /** Fetch Spotify artwork and add as asset */
    const fetchSpotifyArtwork = async (spotifyUrl: string) => {
        try {
            setIsLoading(true);
            const response = await searchSpotifyReleasesAdBox(spotifyUrl);
            console.log('response', response);
            // Handle the API response structure - cast to any to handle type mismatch
            const responseData = response;
            const thumbnailUrl = responseData?.thumbnail_url;
            const name = responseData?.name;
            const artistName = responseData?.primary_artist

            // Store Spotify metadata
            if (name && artistName) {
                setSpotifyMetadata({
                    trackName: name,
                    artistName: artistName,
                    spotifyUrl: spotifyUrl
                });
            }

            if (thumbnailUrl) {
                const spotifyAsset: AdAsset = {
                    id: Date.now().toString(),
                    url: thumbnailUrl,
                    type: 'image',
                    name: `${name || 'Spotify'} - Cover Art`,
                    selected: true,
                    source: 'upload', // Treat as upload so it's synced properly
                };

                // Add the Spotify artwork as the first asset
                setAssets(prev => {
                    // Check if this artwork is already added
                    const exists = prev.some(asset => asset.url === spotifyAsset.url);
                    if (exists) return prev;

                    // Track Spotify artwork added
                    track('ADBOX Asset Uploaded', {
                        version: 'v1',
                        assetType: 'image',
                        assetSource: 'spotify_auto',
                        totalAssets: prev.length + 1,
                        timestamp: new Date().toISOString()
                    });

                    // Add as first asset, keeping max 5 assets
                    return [spotifyAsset, ...prev].slice(0, 5);
                });

                toastSuccess('Spotify artwork added automatically!');
            }
        } catch (error) {
            console.error('Error fetching Spotify artwork:', error);
        } finally {
            setIsLoading(false);
        }
    };

    /** Handle song selection from RecordSearchView */
    const handleSelectRecord = (recordDetails: RecordDetails) => {
        const spotifyUrl = recordDetails.record_url || recordDetails.spotify_uri || '';
        setDestinationLink(spotifyUrl);

        // Track Spotify song selection
        track('ADBOX Spotify Song Selected', {
            version: 'v1',
            trackName: recordDetails.name,
            artistName: recordDetails.primary_artist,
            spotifyId: recordDetails.spotify_id,
            source: 'record_search',
            timestamp: new Date().toISOString()
        });

        // Clear existing assets first
        setAssets([]);

        // Store Spotify metadata
        if (recordDetails.name && recordDetails.primary_artist) {
            setSpotifyMetadata({
                trackName: recordDetails.name,
                artistName: recordDetails.primary_artist,
                spotifyUrl: spotifyUrl
            });
        }

        // Use setLinksToPromote instead of setLinkData
        const linkToPromote = {
            url: spotifyUrl,
            name: recordDetails.name || '',
            isValidUrl: true
        };

        setLinksToPromote([linkToPromote]);
        setSelectedSongToPromote(recordDetails);
        setShowSongSelectorModal(false);
        setHasValidLink(true);

        // Add new asset if thumbnail is available
        if (recordDetails.thumbnail_url) {
            const spotifyAsset: AdAsset = {
                id: `spotify-${Date.now()}-${recordDetails.spotify_id || Math.random()}`,
                url: recordDetails.thumbnail_url,
                type: 'image',
                name: `${recordDetails.name} - Cover Art`,
                selected: true,
                source: 'upload',
            };

            setAssets([spotifyAsset]);
            toastSuccess('Spotify artwork added automatically!');
        } else if (spotifyUrl) {
            // Fallback to fetching if thumbnail_url is not available
            fetchSpotifyArtwork(spotifyUrl);
        }
    };

    /** Handle AdBox song selection with minimal data */
    const handleAdBoxSelect = (data: { url: string; name: string; thumbnailUrl: string; spotifyId: string }) => {
        setDestinationLink(data.url);

        // Clear existing assets first
        setAssets([]);

        // Store Spotify metadata - we might not have artist name here
        if (data.name) {
            setSpotifyMetadata({
                trackName: data.name,
                artistName: '', // Will be populated when fetching artwork
                spotifyUrl: data.url
            });
        }

        // Use setLinksToPromote
        const linkToPromote = {
            url: data.url,
            name: data.name,
            isValidUrl: true
        };

        setLinksToPromote([linkToPromote]);
        setShowSongSelectorModal(false);
        setHasValidLink(true);

        // Add new asset if thumbnail is available
        if (data.thumbnailUrl) {
            const spotifyAsset: AdAsset = {
                id: `spotify-${Date.now()}-${data.spotifyId || Math.random()}`,
                url: data.thumbnailUrl,
                type: 'image',
                name: `${data.name} - Cover Art`,
                selected: true,
                source: 'upload',
            };

            setAssets([spotifyAsset]);
            toastSuccess('Spotify artwork added automatically!');
        }
    };

    /** Handle destination link input */
    const handleDestinationLinkChange = async (value: string) => {
        setDestinationLink(value);

        // Clear assets and metadata when URL changes
        setAssets([]);
        setSpotifyMetadata(null);

        // Only proceed if it's a valid URL
        if (value && isValidUrl(value)) {
            const type = detectLinkType(value);
            const platformType = detectPlatformType(value);

            // Auto-select content type based on URL
            setSelectedContentType(platformType);

            // Use setLinksToPromote instead of setLinkData
            const linkToPromote = {
                url: value,
                name: type === 'spotify' ? 'Spotify Link' : 'Website Link',
                isValidUrl: true,
            };

            setLinksToPromote([linkToPromote]);
            setHasValidLink(true);

            // If it's a Spotify link, fetch the artwork
            console.log('type', type);
            if (type === 'spotify') {
                console.log('fetching Spotify artwork');
                fetchSpotifyArtwork(value);
            }
        } else {
            setLinksToPromote([]);
            setHasValidLink(false);
        }
    };

    /** Handle file upload for assets */
    const handleAssetUpload = (info: CloudinaryUploadedFileType) => {
        const newAsset: AdAsset = {
            id: info.id,
            url: info.secure_url || info.url,
            type: info.resource_type === 'video' ? 'video' : 'image',
            name: info.original_filename || 'Uploaded Asset',
            selected: true,
            source: 'upload',
        };

        // Track asset uploaded
        track('ADBOX Asset Uploaded', {
            version: 'v1',
            assetType: newAsset.type,
            assetSource: 'cloudinary_upload',
            totalAssets: assets.length + 1,
            timestamp: new Date().toISOString()
        });

        setAssets(prev => [...prev, newAsset].slice(-5));
    };

    /** Render upload media button with Cloudinary widget */
    const renderUploadMedia = () => {
        return (
            <>
                <ButtonSymphony
                    iconLeft="upload"
                    id="upload_media_button"
                    className="uploadButton"
                    variant="outlined"
                    style={sty.assetButton}
                    disabled={assets.length >= 5}
                >
                    {mobileView ? 'Upload' : 'Upload Images/Videos'}
                </ButtonSymphony>
                <CloudinaryUploadWidget
                    buttonId="upload_media_button"
                    onUpload={handleAssetUpload}
                />
            </>
        );
    };

    /** Handle link import */
    const handleImportFromLink = async () => {
        if (!linkImportInput) return;

        setIsImportingFromLink(true);
        try {
            const response = await getVideoFromUrl_api(linkImportInput);
            if (response) {
                // Determine platform from URL or import type
                let platform = AssetPlatformType.IMPORTED_FROM_URL;
                let assetName = 'Imported Asset';

                if (linkImportType === 'instagram' || linkImportInput.includes('instagram.com')) {
                    platform = AssetPlatformType.INSTAGRAM;
                    assetName = 'Instagram Post';
                } else if (linkImportType === 'tiktok' || linkImportInput.includes('tiktok.com')) {
                    platform = AssetPlatformType.TIKTOK;
                    assetName = 'TikTok Post';
                }

                const newAsset: AdAsset = {
                    id: response.id,
                    url: response.assetUrl,
                    type: response.mediaType === MediaType.VIDEO ? 'video' : 'image',
                    name: response.platformCaption || assetName,
                    selected: true,
                    source: 'import',
                    platform: platform,
                    thumbnailUrl: response.thumbnailUrl,
                };

                // Track asset imported
                track('ADBOX Asset Uploaded', {
                    version: 'v1',
                    assetType: newAsset.type,
                    assetSource: linkImportType,
                    importPlatform: platform,
                    totalAssets: assets.length + 1,
                    timestamp: new Date().toISOString()
                });

                setAssets(prev => [...prev, newAsset].slice(-5));
                setShowLinkImportModal(false);
                setLinkImportInput('');
                toastSuccess('Asset imported successfully!');
            }
        } catch (error) {
            console.error(error);
            toastError('Failed to import from URL. Please check the link and try again.');
        } finally {
            setIsImportingFromLink(false);
        }
    };

    /** Remove asset */
    const removeAssetLocal = (assetId: string) => {
        setAssets(prev => prev.filter(asset => asset.id !== assetId));
    };

    /** Check if form is valid */
    const isFormValid = (): boolean => {
        return Boolean(linksToPromote.length > 0 && assets.length > 0 && budget >= campaignLength * 10);
    };

    /** Get estimated reach based on budget */
    const getEstimatedReach = () => {
        const clicksPerDollar = 5;
        const clicks = Math.floor(budget * clicksPerDollar);

        if (clicks >= 1000) {
            return `${(clicks / 1000).toFixed(1)}K`;
        }
        return `${clicks}`;
    };

    /** Render link import modal */
    const renderLinkImportModal = () => {
        let modalTitle = 'Import from Social Media';
        let modalSubtitle = 'Paste a link to import content';
        let placeholder = 'https://...';

        if (linkImportType === 'instagram') {
            modalTitle = 'Import Instagram Post';
            modalSubtitle = 'Paste an Instagram post link';
            placeholder = 'https://www.instagram.com/p/...';
        } else if (linkImportType === 'tiktok') {
            modalTitle = 'Import TikTok Post';
            modalSubtitle = 'Paste a TikTok video link';
            placeholder = 'https://www.tiktok.com/@username/video/...';
        }

        return (
            <ModalSymphony
                className="linkImportModal"
                isOpen={showLinkImportModal}
                title={modalTitle}
                subtitle={modalSubtitle}
                onClose={() => {
                    setShowLinkImportModal(false);
                    setLinkImportInput('');
                    setLinkImportType('general');
                }}
                buttonLeftText="Cancel"
                buttonRightText="Import"
                buttonLeftVariant="outlined"
                onClickButtonLeft={() => {
                    setShowLinkImportModal(false);
                    setLinkImportInput('');
                    setLinkImportType('general');
                }}
                onClickButtonRight={handleImportFromLink}
                buttonRightDisabled={!linkImportInput || isImportingFromLink}
                buttonRightIsLoading={isImportingFromLink}
            >
                <div className="linkImportContent" style={sty.linkImportContent}>
                    <InputSymphony
                        className="linkImportInput"
                        value={linkImportInput}
                        onChange={(value) => setLinkImportInput(value as string)}
                        placeholder={placeholder}
                        onEnter={handleImportFromLink}
                        disabled={isImportingFromLink}
                    />
                </div>
            </ModalSymphony>
        );
    };

    /** Render song selector modal */
    const renderSongSelectorModal = () => {
        return (
            <ModalSymphony
                className="songSelectorModal"
                isOpen={showSongSelectorModal}
                title="Select Music to Promote"
                subtitle="Choose a song, album, or playlist"
                onClose={() => setShowSongSelectorModal(false)}
                size="medium"
            >
                <RecordSearchView
                    handleSelectRecord={handleSelectRecord}
                    handleAdBoxSelect={handleAdBoxSelect}
                    options={['single', 'project', 'playlist']}
                    letUserSearchSpotify
                    maxHeight={400}
                    isAdBox={true}
                />
            </ModalSymphony>
        );
    };

    /** Get label text based on content type */
    const getLabelText = (contentType: typeof selectedContentType): string => {
        switch (contentType) {
            case 'music':
                return 'Enter your Spotify link';
            case 'podcast':
                return 'Enter your podcast link';
            case 'youtube':
                return 'Enter your YouTube link';
            case 'shopify':
                return 'Enter your Shopify link';
            case 'event':
                return 'Enter your event link';
            case 'ticket':
                return 'Enter your ticket link';
            case 'newsletter':
                return 'Enter your newsletter link';
            case 'website':
                return 'Enter your website link';
            default:
                return '';
        }
    };

    /** Render content type selector */
    const renderContentTypeSelector = () => {
        const contentTypes = [
            {
                type: 'music' as const,
                icon: 'spotifyFlat',
                label: 'Music',
                subLabel: 'Spotify & Streaming',
                color: TagColors.purple,
                onClick: () => setShowSongSelectorModal(true)
            },
            {
                type: 'youtube' as const,
                icon: 'youtubeFlat',
                label: 'Video',
                subLabel: 'YouTube & Clips',
                color: TagColors.red
            },
            {
                type: 'podcast' as const,
                icon: 'headphones',
                label: 'Podcast',
                subLabel: 'Episodes & Shows',
                color: TagColors.green
            },
            {
                type: 'event' as const,
                icon: 'calendar',
                label: 'Event',
                subLabel: 'Concerts & Shows',
                color: TagColors.yellow
            },
            {
                type: 'ticket' as const,
                icon: 'crown',
                label: 'Tickets',
                subLabel: 'Sales & RSVP',
                color: TagColors.yellow
            },
            {
                type: 'shopify' as const,
                icon: 'gift',
                label: 'Shopify Product',
                subLabel: 'Merch & Items',
                color: TagColors.green
            },
            {
                type: 'newsletter' as const,
                icon: 'mail',
                label: 'Newsletter',
                subLabel: 'Email List',
                color: TagColors.purple
            },
            {
                type: 'website' as const,
                icon: 'website',
                label: 'Website',
                subLabel: 'Any Link',
                color: TagColors.grey
            },
        ];

        const handleContentTypeSelect = (type: typeof selectedContentType) => {
            if (type !== selectedContentType) {
                // Track content type selection
                track('ADBOX Content Type Selected', {
                    version: 'v1',
                    contentType: type,
                    previousType: selectedContentType,
                    timestamp: new Date().toISOString()
                });

                // Trigger label animation sequence
                const newLabelText = getLabelText(type);
                setLabelText(newLabelText);
                setLabelKey(prev => prev + 1);
                setSelectedContentType(type);

                // Clear the input when switching types
                if (!destinationLink) {
                    setDestinationLink('');
                }
            }
        };

        return (
            <motion.div
                className="contentTypeSelector"
                style={mStyle(sty, 'contentTypeSelector')}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
            >
                <div style={mStyle(sty, 'contentTypeSelectorTitle')}>Or choose what you're promoting:</div>
                <div className="contentTypeOptions" style={mStyle(sty, 'contentTypeOptions')}>
                    {contentTypes.map(({ type, icon, label, subLabel, color, onClick }) => (
                        <motion.div
                            key={type}
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                            style={sty.contentTypeWrapper}
                        >
                            <TagSymphony
                                className="contentTypeTag"
                                style={styx(
                                    mStyle(sty, 'contentTypeTag'),
                                    selectedContentType === type && sty.contentTypeTagSelected
                                )}
                                onClick={() => {
                                    if (onClick) {
                                        onClick();
                                    } else {
                                        handleContentTypeSelect(type);
                                    }
                                }}
                                icon={icon}
                                iconColor={color}
                                color={selectedContentType === type ? color : TagColors.grey}
                                type="rounded"
                                isBorder={true}
                                id={type}
                                title={mobileView ? label : `${label}`}
                            />
                        </motion.div>
                    ))}
                </div>
            </motion.div>
        );
    };

    /** Render progress indicator */
    const renderProgressIndicator = () => {
        const steps = [
            { label: 'Link', isComplete: hasValidLink },
            { label: 'Assets', isComplete: assets.length > 0 && hasValidLink },
            { label: 'Budget', isComplete: budget >= campaignLength * 10 && assets.length > 0 }
        ];

        return (
            <motion.div
                className="progressIndicator"
                style={mStyle(sty, 'progressIndicator')}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
            >
                {steps.map((step, index) => (
                    <React.Fragment key={index}>
                        <div style={sty.progressStepContainer}>
                            <div
                                style={styx(
                                    mStyle(sty, 'progressDot'),
                                    step.isComplete && sty.progressDotComplete
                                )}
                            >
                                {step.isComplete && <BiCheck size={mobileView ? 12 : 16} />}
                            </div>
                            {!mobileView && (
                                <div style={mStyle(sty, 'progressLabel')}>{step.label}</div>
                            )}
                        </div>
                        {index < steps.length - 1 && (
                            <div
                                style={styx(
                                    mStyle(sty, 'progressLine'),
                                    step.isComplete && sty.progressLineComplete
                                )}
                            />
                        )}
                    </React.Fragment>
                ))}
            </motion.div>
        );
    };

    /** Render asset buttons section */
    const renderAssetButtons = () => {
        const assetOptions = [
            {
                icon: 'upload',
                label: mobileView ? 'Upload' : 'Upload Media',
                onClick: () => { },
                render: renderUploadMedia
            },
            {
                icon: 'growInstagramEngagement',
                label: mobileView ? 'Import IG' : 'Import IG Post',
                onClick: () => {
                    setLinkImportType('instagram');
                    setShowLinkImportModal(true);
                }
            },
            {
                icon: 'tiktokExternalTrafficV2',
                label: mobileView ? 'Import TikTok' : 'Import TikTok Post',
                onClick: () => {
                    setLinkImportType('tiktok');
                    setShowLinkImportModal(true);
                }
            },
            // {
            //     icon: 'link',
            //     label: mobileView ? 'Import URL' : 'Import from URL',
            //     onClick: () => {
            //         setLinkImportType('general');
            //         setShowLinkImportModal(true);
            //     }
            // }
        ];

        return (
            <div className="assetButtons" style={mStyle(sty, 'assetButtons')}>
                {assetOptions.map((option, index) => {
                    if (option.render) {
                        return option.render();
                    }
                    return (
                        <ButtonSymphony
                            key={index}
                            iconLeft={option.icon}
                            className="importButton"
                            onClick={option.onClick}
                            variant="outlined"
                            style={mStyle(sty, 'assetButton')}
                        >
                            {option.label}
                        </ButtonSymphony>
                    );
                })}
            </div>
        );
    };

    const renderBudgetDurationSection = () => {
        return (
            <motion.section
                className="budgetDurationSection"
                style={styx(mStyle(sty, 'section'))}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ delay: 0.2, duration: 0.4 }}
            >
                <header className="budgetDurationSectionHeader" style={mStyle(sty, 'sectionHeader')}>
                    <div className="budgetDurationSectionIcon" style={styx(mStyle(sty, 'sectionIcon'), sty.sectionIconCompleteBudget)}>
                        <BiDollar size={mobileView ? 18 : 20} />
                    </div>
                    <div>
                        <div style={mobileView ? Sty.text14bold : Sty.text16bold}>Budget & Duration</div>
                        <div style={Sty.text12grey}>Set your campaign investment</div>
                    </div>
                </header>

                <div className="budgetMetrics" style={mStyle(sty, 'budgetMetrics')}>
                    <div style={mStyle(sty, 'metricItem')}>
                        <div style={mStyle(sty, 'metricValue')}>${dailyBudget}</div>
                        <div style={mStyle(sty, 'metricLabel')}>per day</div>
                    </div>
                    <div style={styx(mStyle(sty, 'metricItem'), sty.metricItemMiddle)}>
                        <div style={mStyle(sty, 'metricValue')}>${budget}</div>
                        <div style={mStyle(sty, 'metricLabel')}>total budget</div>
                    </div>
                    <div style={styx(mStyle(sty, 'metricItem'), sty.metricItemHighlight)}>
                        <div style={mStyle(sty, 'clicksMetricContainer')}>
                            <HiCursorClick size={mobileView ? 20 : 24} style={sty.clickIcon} />
                            <div style={mStyle(sty, 'metricValueHighlight')}>{getEstimatedReach()}</div>
                        </div>
                        <div style={mStyle(sty, 'metricLabelHighlight')}>estimated clicks</div>
                    </div>
                </div>

                <SliderSymphony
                    className="budgetSlider"
                    style={sty.slider}
                    sliderOffset={10}
                    value={budget}
                    min={50}
                    max={500}
                    marks={budgetMarks}
                    snapToMarks
                    onChange={(value: number | number[]) => setBudgetLocal(Array.isArray(value) ? value[0] : value)}
                    equidistant
                />

                <div className="durationSection" style={mStyle(sty, 'durationSection')}>
                    <div style={Sty.text14}>Campaign Length</div>
                    <InputSymphony
                        className="campaignDayDropdown"
                        value={campaignLength.toString()}
                        onOptionSelect={(value: SelectOptionItem) => handleCampaignLengthChange(value.value.toString())}
                        isSelectDropdown
                        options={campaignLengthOptions.map(opt => ({
                            value: opt.value,
                            title: opt.title
                        }))}
                        maxWidth={mobileView ? '100%' : 160}
                    />
                </div>
            </motion.section>
        )
    }

    const renderCreativeAssetsSection = () => {
        return (
            <motion.section
                className="creativeAssetsSection"
                style={styx(mStyle(sty, 'section'))}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ delay: 0.1, duration: 0.4 }}
            >
                <header className="creativeAssetsSectionHeader" style={mStyle(sty, 'sectionHeader')}>
                    <div style={styx(mStyle(sty, 'sectionIcon'), assets.length > 0 ? sty.sectionIconCompleteAssets : sty.sectionIconIncompleteAssets)}>
                        {assets.length > 0 ? <BiCheck size={mobileView ? 18 : 20} /> : <BiImage size={mobileView ? 18 : 20} />}
                    </div>
                    <div>
                        <div style={mobileView ? Sty.text14bold : Sty.text16bold}>Creative Assets</div>
                        <div style={Sty.text12grey}>Upload images or videos for your ad</div>
                    </div>
                </header>

                {renderAssetButtons()}

                {assets.length > 0 && (
                    <motion.div
                        className="assetsList"
                        style={sty.assetsList}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                    >
                        {assets.map((asset) => (
                            <motion.div
                                key={asset.id}
                                className="assetItem"
                                style={mStyle(sty, 'assetItem')}
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                            >
                                {asset.type === 'video' ? (
                                    <video src={asset.url} style={sty.assetThumb} />
                                ) : (
                                    <img src={asset.url} alt={asset.name} style={sty.assetThumb} />
                                )}
                                <div
                                    className="removeAsset"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        removeAssetLocal(asset.id);
                                    }}
                                    style={sty.removeBtn}
                                >
                                    <BiX size={18} />
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                )}
            </motion.section>
        )
    }

    const renderApprovedLinkSection = () => {
        const isSpotifyLink = detectLinkType(destinationLink) === 'spotify';

        // Extract domain name for non-Spotify links
        const getDomainName = (url: string): string => {
            try {
                const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
                return urlObj.hostname.replace('www.', '');
            } catch {
                return url;
            }
        };

        return (
            <motion.section
                className="approvedLinkSection"
                style={styx(mStyle(sty, 'section'))}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ delay: 0.1, duration: 0.4 }}
            >
                <header className="approvedLinkSectionHeader" style={mStyle(sty, 'sectionHeader')}>
                    <div style={styx(mStyle(sty, 'sectionIcon'), sty.sectionIconCompleteLink)}>
                        <BiCheck size={mobileView ? 18 : 20} />
                    </div>
                    <div>
                        <div style={mobileView ? Sty.text14bold : Sty.text16bold}>Promoted Link</div>
                        <div style={Sty.text12grey}>
                            {isSpotifyLink ? 'Your Spotify track' : 'Your website link'}
                        </div>
                    </div>
                </header>

                {isSpotifyLink && spotifyMetadata ? (
                    <div style={mStyle(sty, 'spotifyLinkContainer')}>
                        {/* Show artwork if available */}
                        {assets.length > 0 && assets[0].type === 'image' && (
                            <img
                                src={assets[0].url}
                                alt={spotifyMetadata.trackName}
                                style={mStyle(sty, 'spotifyArtwork')}
                            />
                        )}
                        <div style={sty.spotifyTrackInfo}>
                            <div style={mStyle(sty, 'spotifyTrackName')}>{spotifyMetadata.trackName}</div>
                            <div style={mStyle(sty, 'spotifyArtistName')}>{spotifyMetadata.artistName}</div>
                            <div style={mStyle(sty, 'spotifyLinkUrl')}>{destinationLink}</div>
                        </div>
                    </div>
                ) : (
                    <div style={sty.linkContainer}>
                        <div style={sty.linkDomain}>{getDomainName(destinationLink)}</div>
                        <div style={sty.linkText}>{destinationLink}</div>
                    </div>
                )}
            </motion.section>
        )
    }

    /** Handle campaign submission with analytics */
    const handleSubmitCampaign = async () => {
        // Track submit clicked with full context
        track('ADBOX Submit Clicked', {
            version: 'v1',
            budget: budget,
            dailyBudget: dailyBudget,
            campaignLength: campaignLength,
            assetCount: assets.length,
            link: destinationLink,
            linkType: detectLinkType(destinationLink),
            platformType: detectPlatformType(destinationLink),
            hasSpotifyMetadata: !!spotifyMetadata,
            assetTypes: assets.map(asset => asset.type),
            assetSources: assets.map(asset => asset.source),
            selectedContentType: selectedContentType,
            timestamp: new Date().toISOString()
        });

        // Call the original submit function
        await submitCampaignFromAdBox();
    };

    /** Handle link blur for analytics tracking */
    const handleLinkBlur = (value: string) => {
        // Only track valid, complete URLs when user finishes input
        if (value && isValidUrl(value)) {
            const type = detectLinkType(value);
            const platformType = detectPlatformType(value);
            
            track('ADBOX Link Inputted', {
                version: 'v1',
                link: value,
                linkType: type,
                platformType: platformType,
                timestamp: new Date().toISOString()
            });
        }
    };

    return (
        <div
            className={clsx('adCreatorPageComponent', className)}
            style={styx(mStyle(sty, 'adCreatorPageComponent'), style)}
        >
            <style>
                {`
                    .assetItem:hover .removeAsset {
                        opacity: 1 !important;
                    }
                    .assetButton:hover {
                        border-color: ${Colors.purple} !important;
                        background-color: rgba(136, 0, 255, 0.05) !important;
                    }
                    .searchSpotifyButton:hover {
                        border-color: ${Colors.purple} !important;
                        background-color: rgba(136, 0, 255, 0.05) !important;
                        color: ${Colors.purple} !important;
                    }
                    .submitButton:hover:not(:disabled) {
                        transform: translateY(-2px);
                        box-shadow: 0 6px 30px rgba(136, 0, 255, 0.4) !important;
                    }
                    .submitButton:disabled {
                        background: ${Colors.greyLight} !important;
                        box-shadow: none !important;
                        cursor: not-allowed;
                    }
                    .destinationInput:focus-within {
                        box-shadow: 0 0 120px 0 rgba(136, 0, 255, 0.75) !important;
                        border-radius: 200px !important;
                    }
                `}
            </style>

            {/* Progress Indicator - Show when link is valid */}
            {hasValidLink && renderProgressIndicator()}

            <motion.div
                className="layoutContainer"
                style={styx(mStyle(sty, 'layoutContainer'), styleLayout)}
                initial={false}
                animate={hasValidLink ? 'expanded' : 'centered'}
            >
                <motion.div
                    className="formContainer"
                    style={mStyle(sty, 'formContainer')}
                    layout
                >
                    {/* Destination Link Section - Always visible */}
                    <motion.div
                        className="destinationSection"
                        style={mStyle(sty, 'destinationSection')}
                        layout
                    >
                        <motion.div
                            className="linkInputContainer"
                            style={mStyle(sty, 'linkInputContainer')}
                            layout
                        >
                            {/* Animated Label */}
                            {selectedContentType && labelText && (
                                <motion.div
                                    key={labelKey}
                                    className="inputLabel"
                                    style={mStyle(sty, 'inputLabel')}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 8 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{
                                        duration: 0.4,
                                        ease: [0.4, 0, 0.2, 1]
                                    }}
                                >
                                    {labelText}
                                </motion.div>
                            )}

                            <InputSymphony
                                id="inputBox"
                                className="destinationInput"
                                styleInput={mStyle(sty, 'destinationInput')}
                                value={destinationLink}
                                onChange={handleDestinationLinkChange}
                                onBlur={handleLinkBlur}
                                placeholder={getPlaceholderText()}
                                isLoading={isLoading}
                            />
                            {!hasValidLink && renderContentTypeSelector()}
                        </motion.div>
                    </motion.div>

                    {/* Additional sections - Only shown when link is valid */}
                    {hasValidLink && (
                        <motion.div
                            className="additionalSections"
                            style={mStyle(sty, 'additionalSections')}
                            key="additional-sections"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            {/* Approved Link Section */}
                            {renderApprovedLinkSection()}

                            {/* Creative Assets Section */}
                            {renderCreativeAssetsSection()}

                            {/* Budget & Duration Section */}
                            {renderBudgetDurationSection()}

                            {/* Submit Button */}
                            <motion.div
                                className="submitSectionButtonContainer"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 20 }}
                                transition={{ delay: 0.3, duration: 0.4 }}
                                style={mStyle(sty, 'submitSectionButtonContainer')}
                            >
                                <div style={styx(Sty.text14white, { textAlign: 'center' })}>
                                    20% of your budget will be charged as an automation fee. {isFormValid() ? 'Complete all steps to launch your campaign' : 'Complete all steps to launch your campaign'}
                                </div>
                                <ButtonSymphony
                                    className="inputBoxSubmitButton"
                                    style={mStyle(sty, 'inputBoxSubmitButton')}
                                    onClick={handleSubmitCampaign}
                                    variant="primary"
                                    color="purple"
                                    disabled={!isFormValid() || isSubmittingCampaign}
                                    isLoading={isSubmittingCampaign}
                                    size={'large'}
                                >
                                    Launch Campaign
                                </ButtonSymphony>

                            </motion.div>
                        </motion.div>
                    )}
                </motion.div>

                {/* Ad preview (desktop only) - shows after link is valid */}
                {!mobileView && hasValidLink && (
                    <motion.aside
                        key="preview-column"
                        className="previewColumn"
                        initial={{ opacity: 0, x: 40 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 40 }}
                        transition={{ delay: 0.4, duration: 0.5 }}
                        style={sty.previewColumn}
                    >
                        <div className="previewSticky" style={sty.previewSticky}>
                            <AdPreviewCampaignView
                                className="adPreviewCampaignViewFromAdBoxDesktop"
                                style={sty.adPreviewCampaignViewFromAdBoxDesktop}
                                variant="facebook"
                                hideTitle={false}
                                hideSubtitle={false}
                                hidePlatformSelector={false}
                            />
                        </div>
                    </motion.aside>
                )}
            </motion.div>

            {/* Mobile ad preview */}
            {mobileView && hasValidLink && (
                <motion.div
                    key="mobile-preview"
                    className="mobilePreviewSection"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    transition={{ delay: 0.4 }}
                >
                    <AdPreviewCampaignView
                        className="adPreviewCampaignViewFromAdBoxMobile"
                        style={sty.adPreviewCampaignViewFromAdBoxMobile}
                        variant="facebook"
                        hideTitle={false}
                        hideSubtitle={false}
                        hidePlatformSelector={false}
                    />
                </motion.div>
            )}

            {renderLinkImportModal()}
            {renderSongSelectorModal()}
        </div>
    );
};

const sty: Record<string, CSSProperties> = {
    additionalSections: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
    },
    additionalSections_mobile: {
        gap: 12,
    },
    adCreatorPageComponent: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    adCreatorPageComponent_mobile: {
        minHeight: 'auto',
    },
    layoutContainer: {
        display: 'flex',
        gap: 40,
        paddingTop: 40,
        paddingBottom: 40,
        paddingLeft: 40,
        paddingRight: 40,
        width: '100%',
        maxWidth: 1200,
        margin: '0 auto',
        justifyContent: 'center',
        alignItems: 'flex-start',
    },
    layoutContainer_mobile: {
        flexDirection: 'column',
        gap: 20,
        paddingTop: 32,
        paddingBottom: 32,
        paddingLeft: 0,
        paddingRight: 0,
    },
    formContainer: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 20,
        overflow: 'visible',
        padding: 16,
        maxWidth: 800,
        width: '100%',
        gap: 24,
    },
    formContainer_mobile: {
        borderRadius: 16,
        padding: 0,
    },
    destinationSection: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        marginBottom: 0,
    },
    destinationSection_mobile: {
        marginBottom: 0,
    },
    mainTitle: {
        fontSize: 32,
        fontWeight: 500,
        color: Colors.black,
        marginBottom: 8,
    },
    mainTitle_mobile: {
        fontSize: 24,
    },
    subtitle: {
        fontSize: 16,
        color: Colors.grey,
        marginBottom: 32,
    },
    subtitle_mobile: {
        fontSize: 14,
        marginBottom: 24,
    },
    linkInputContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
        width: '100%',
    },
    linkInputContainer_mobile: {
        gap: 12,
    },
    destinationInput: {
        fontSize: 24,
        height: 72,
        padding: 24,
        borderWidth: 2,
        borderColor: Colors.purple,
        backgroundColor: Colors.purpleTransparent,
        borderRadius: 100,
        color: Colors.white,
        transition: 'all 0.3s ease',
        width: '100%',
        textAlign: 'left',
        fontWeight: 400,
        boxShadow: 'rgba(136, 0, 255, 0.1) 0px 10px 40px',
    },
    destinationInput_mobile: {
        fontSize: 16,
        height: 72,
        paddingLeft: 20,
        paddingRight: 20,
    },
    searchSpotifyButton: {
        backgroundColor: 'white',
        border: `2px solid ${Colors.white}`,
        transition: 'all 0.2s ease',
        color: Colors.black,
    },
    section: {
        backgroundColor: Colors.greyLighter,
        borderRadius: 16,
        padding: 16,
        border: `2px solid ${Colors.greyLight}`,
        transition: 'all 0.3s ease',
        position: 'relative',
        overflow: 'visible',
        gap: 16,
        display: 'flex',
        flexDirection: 'column',
    },
    section_mobile: {
        padding: 8,
        borderRadius: 10,
    },
    sectionCompleteLink: {
        borderColor: Colors.purple,
        backgroundColor: 'rgba(136, 0, 255, 0.03)',
    },
    sectionIncompleteLink: {
        borderColor: Colors.greyLight,
    },
    sectionCompleteAssets: {
        borderColor: Colors.blue,
    },
    sectionIncompleteAssets: {
        borderColor: Colors.greyLight,
    },
    sectionCompleteBudget: {
        borderColor: Colors.green,
        backgroundColor: 'rgba(76, 175, 80, 0.03)',
    },
    sectionHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
    },
    sectionHeader_mobile: {
        gap: 10,
        marginBottom: 0,
    },
    sectionIcon: {
        width: 36,
        height: 36,
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.3s ease',
        flexShrink: 0,
    },
    sectionIcon_mobile: {
        width: 32,
        height: 32,
        borderRadius: 8,
    },
    sectionIconCompleteLink: {
        backgroundColor: Colors.purple,
        color: 'white',
    },
    sectionIconIncompleteLink: {
        backgroundColor: Colors.greyLight,
        color: Colors.grey,
    },
    sectionIconCompleteAssets: {
        backgroundColor: Colors.blue,
        color: 'white',
    },
    sectionIconIncompleteAssets: {
        backgroundColor: Colors.greyLight,
        color: Colors.grey,
    },
    sectionIconCompleteBudget: {
        backgroundColor: Colors.green,
        color: 'white',
    },
    linkInputRow: {
        display: 'flex',
        gap: 12,
    },
    input: {
        fontSize: 14,
        backgroundColor: 'white',
        flex: 1,
    },
    linkPreview: {
        display: 'flex',
        gap: 12,
        padding: 12,
        backgroundColor: 'white',
        borderRadius: 10,
        alignItems: 'center',
        marginTop: 12,
        border: `1px solid ${Colors.greyLight}`,
    },
    linkPreview_mobile: {
        padding: 10,
        gap: 10,
    },
    linkThumbnail: {
        width: 40,
        height: 40,
        borderRadius: 6,
        objectFit: 'cover',
    },
    linkThumbnail_mobile: {
        width: 36,
        height: 36,
        borderRadius: 6,
    },
    linkInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        overflow: 'hidden',
    },
    linkContainer: {
        padding: 12,
        backgroundColor: 'white',
        borderRadius: 8,
        border: `1px solid ${Colors.greyLight}`,
    },
    linkText: {
        fontSize: 14,
        color: Colors.grey,
        wordBreak: 'break-all',
    },
    assetButtons: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    assetButtons_mobile: {
        gap: 8,
    },
    assetButton: {
        height: 44,
        backgroundColor: 'white',
        border: `2px dashed ${Colors.greyLight}`,
        transition: 'all 0.2s ease',
        fontSize: 14,
    },
    assetsList: {
        display: 'flex',
        gap: 10,
        marginTop: 12,
        overflowX: 'auto',
        padding: 2,
    },
    assetItem: {
        position: 'relative',
        width: 72,
        height: 72,
        borderRadius: 10,
        overflow: 'hidden',
        border: `3px solid transparent`,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        flexShrink: 0,
    },
    assetItem_mobile: {
        width: 60,
        height: 60,
        borderRadius: 8,
    },
    assetThumb: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    removeBtn: {
        position: 'absolute',
        top: 4,
        right: 4,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        color: 'white',
        width: 24,
        height: 24,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 0,
        transition: 'opacity 0.2s ease',
        cursor: 'pointer',
    },
    budgetInfo: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 16,
        backgroundColor: 'white',
        borderRadius: 8,
        border: `1px solid ${Colors.greyLight}`,
    },
    budgetInfo_mobile: {
        padding: 12,
        gap: 12,
    },
    budgetMain: {
        textAlign: 'center',
    },
    budgetStats: {
        display: 'flex',
        gap: 24,
    },
    budgetStats_mobile: {
        gap: 20,
    },
    statItem: {
        textAlign: 'center',
    },
    sliderSection: {
        marginBottom: 24,
    },
    slider: {
    },
    durationSection: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        overflow: 'visible',
    },
    durationSection_mobile: {
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 8,
    },
    submitSectionButtonContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
        paddingTop: 8,
        paddingBottom: 24,
    },
    submitSectionButtonContainer_mobile: {
        // padding: 0,
    },
    inputBoxSubmitButton: {
        width: '100%',
        background: `linear-gradient(135deg, ${Colors.purple} 0%, ${Colors.purpleDark} 100%)`,
        boxShadow: '0 4px 20px rgba(136, 0, 255, 0.3)',
        transition: 'all 0.3s ease',
        height: 60,
    },
    inputBoxSubmitButton_mobile: {
        width: '100%',
        paddingLeft: 24,
        paddingRight: 24,
        height: 60,
        fontSize: 24,
    },
    linkImportContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    previewColumn: {
        width: 340,
    },
    previewSticky: {
        position: 'sticky',
        top: 40,
    },
    spotifyLinkContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: 12,
        backgroundColor: 'white',
        borderRadius: 8,
        border: `1px solid ${Colors.greyLight}`,
    },
    spotifyLinkContainer_mobile: {
        padding: 10,
        gap: 10,
    },
    spotifyTrackInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        flex: 1,
    },
    spotifyTrackName: {
        fontSize: 16,
        fontWeight: 600,
        color: Colors.black,
        lineHeight: 1.2,
    },
    spotifyTrackName_mobile: {
        fontSize: 14,
    },
    spotifyArtistName: {
        fontSize: 14,
        color: Colors.grey,
        lineHeight: 1.2,
    },
    spotifyArtistName_mobile: {
        fontSize: 12,
    },
    spotifyLinkUrl: {
        fontSize: 12,
        color: Colors.greyDark,
        wordBreak: 'break-all',
        lineHeight: 1.3,
        marginTop: 4,
    },
    spotifyLinkUrl_mobile: {
        fontSize: 11,
    },
    spotifyArtwork: {
        width: 56,
        height: 56,
        borderRadius: 8,
        objectFit: 'cover',
        flexShrink: 0,
    },
    spotifyArtwork_mobile: {
        width: 48,
        height: 48,
        borderRadius: 6,
    },
    linkDomain: {
        fontSize: 16,
        fontWeight: 600,
        color: Colors.black,
        lineHeight: 1.3,
        marginBottom: 4,
    },
    budgetMetrics: {
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'space-between',
        gap: 0,
        backgroundColor: 'white',
        borderRadius: 12,
        border: `1px solid ${Colors.greyLight}`,
        overflow: 'hidden',
    },
    budgetMetrics_mobile: {
        flexDirection: 'row',
        gap: 0,
    },
    metricItem: {
        flex: 1,
        padding: 20,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 8,
    },
    metricItem_mobile: {
        padding: 16,
    },
    metricItemMiddle: {
        borderLeft: `1px solid ${Colors.greyLight}`,
        borderRight: `1px solid ${Colors.greyLight}`,
    },
    metricItemMiddle_mobile: {
        borderLeft: 'none',
        borderRight: 'none',
        borderTop: `1px solid ${Colors.greyLight}`,
        borderBottom: `1px solid ${Colors.greyLight}`,
    },
    metricItemHighlight: {
        backgroundColor: 'rgba(136, 0, 255, 0.05)',
        position: 'relative',
    },
    clicksMetricContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
    },
    clickIcon: {
        color: Colors.purple,
    },
    metricValue: {
        fontSize: 28,
        fontWeight: 700,
        color: Colors.black,
        lineHeight: 1,
    },
    metricValue_mobile: {
        fontSize: 24,
    },
    metricLabel: {
        fontSize: 13,
        color: Colors.greyDark,
        fontWeight: 500,
    },
    metricLabel_mobile: {
        fontSize: 12,
    },
    metricValueHighlight: {
        fontSize: 28,
        fontWeight: 700,
        color: Colors.purple,
        lineHeight: 1,
    },
    metricValueHighlight_mobile: {
        fontSize: 24,
    },
    metricLabelHighlight: {
        fontSize: 13,
        color: Colors.purple,
        fontWeight: 600,
    },
    metricLabelHighlight_mobile: {
        fontSize: 12,
    },
    adPreviewCampaignViewFromAdBoxDesktop: {
        borderRadius: 16,
    },
    adPreviewCampaignViewFromAdBoxMobile: {
        borderRadius: 16,
        paddingTop: 16,
        paddingBottom: 16,
        paddingLeft: 16,
        paddingRight: 16,
    },
    contentTypeSelector: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        width: '100%',
    },
    contentTypeSelector_mobile: {
        gap: 10,
    },
    contentTypeSelectorTitle: {
        fontSize: 14,
        fontWeight: 500,
        color: Colors.grey,
        textAlign: 'center',
        marginBottom: 8,
    },
    contentTypeSelectorTitle_mobile: {
        fontSize: 13,
        marginBottom: 6,
    },
    contentTypeOptions: {
        display: 'flex',
        flexDirection: 'row',
        gap: 12,
        flexWrap: 'wrap',
        justifyContent: 'center',
    },
    contentTypeOptions_mobile: {
    },
    contentTypeWrapper: {
        cursor: 'pointer',
    },
    contentTypeTag: {
        minWidth: 120,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        display: 'inline-flex',
        fontSize: 18,
        padding: 24,
    },
    contentTypeTag_mobile: {
        minWidth: 100,
        padding: 18,
        fontSize: 18,
    },
    contentTypeTagSelected: {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(136, 0, 255, 0.2)',
    },
    contentTypeTagContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
    },
    contentTypeTagContent_mobile: {
        gap: 0,
    },
    contentTypeLabel: {
        fontSize: 13,
        fontWeight: 600,
    },
    contentTypeLabel_mobile: {
        fontSize: 12,
    },
    contentTypeSubLabel: {
        fontSize: 11,
        fontWeight: 400,
        opacity: 0.8,
    },
    progressIndicator: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 32,
        paddingTop: 20,
        gap: 0,
    },
    progressIndicator_mobile: {
        marginBottom: 24,
        paddingTop: 16,
        paddingLeft: 20,
        paddingRight: 20,
    },
    progressStepContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
    },
    progressDot: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        backgroundColor: Colors.greyLight,
        border: `2px solid ${Colors.greyLight}`,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        transition: 'all 0.3s ease',
        color: Colors.grey,
    },
    progressDot_mobile: {
        width: 24,
        height: 24,
    },
    progressDotComplete: {
        backgroundColor: Colors.purple,
        borderColor: Colors.purple,
        color: 'white',
    },
    progressLine: {
        width: 80,
        height: 2,
        backgroundColor: Colors.greyLight,
        marginTop: -30,
        transition: 'all 0.3s ease',
    },
    progressLine_mobile: {
        width: 40,
        marginTop: -3,
    },
    progressLineComplete: {
        backgroundColor: Colors.purple,
    },
    progressLabel: {
        fontSize: 12,
        color: Colors.grey,
        fontWeight: 500,
        marginTop: 4,
    },
    inputLabel: {
        fontSize: 16,
        fontWeight: 500,
        color: Colors.white,
        marginBottom: 0,
        textAlign: 'left',
        letterSpacing: 0.5,
        width: '100%',
        paddingLeft: 20,
    },
    inputLabel_mobile: {
        fontSize: 14,
        marginBottom: 6,
    },
};

export default AdCreatorPage; 